import styled from '@emotion/styled';
import {
  atom,
  useSetAtom,
} from 'jotai';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import SignPage from './page';
import SignTab from './tab';
import { useSignPageControl } from './use-sign-page-control';
import Divider from '@/components/comm/Divider';
import ImageButton from '@/components/comm/ImageButton';
import Page from '@/components/comm/Page';
import { breakpointMedium, breakpointSmall } from '@/constants/breakpoints';
import { alphaHex, colors } from '@/constants/colors';
import { createSigendInCheckGetServerSideProps } from '@/lib/create-route-server-side-props';
import { showToast } from '@/lib/toast';
import icGnbWhiteLogo from '@/public/images/ic-gnb-white-logo.svg';
import imgLoginIllust from '@/public/images/img-login-illust.svg';

type SignProps = {
  alreadySignedIn: boolean,
};

const SignBaseDiv = styled.div`
display: flex;
min-height: 100vh;
justify-content: center;
background-color: ${colors.darkBlue2};
`;

const SignLayoutDiv = styled.div`
display: grid;
grid-template-columns: 1fr 1fr;
grid-template-rows: 80px;

@media (max-width: ${breakpointMedium}) {
  grid-template-columns: 1fr;
}
`;

const TopDiv = styled.div`
display: flex;
min-height: 80px;
align-items: center;
justify-content: space-between;
grid-column: 1 / 3;
`;

const GroupSignUpButton = styled.button`
all: unset;
margin-right: 40px;
color: ${colors.white};
cursor: pointer;
font-size: 14px;
font-weight: 700;

@media (max-width: ${breakpointSmall}) {
  margin-right: 0px;
}
`;

const LeftSideSection = styled.section`
display: flex;
flex-direction: column;
padding-top: 80px;
padding-left: 40px;
gap: 18px;
grid-column: 1 / 2;

@media (max-width: ${breakpointMedium}) {
  padding-top: 20px;
}

@media (max-width: ${breakpointSmall}) {
  display: none;
}
`;

const TitleSpan = styled.span`
color: ${colors.white};
font-size: 25px;
font-weight: 700;
line-height: 36px;
`;

const SubTitleSpan = styled.span`
color: ${colors.white}${alphaHex(0.7)};
font-size: 16px;
font-weight: 700;
line-height: 23px;
`;

const ImgContainer = styled.div`
margin-top: -140px;
margin-left: -80px;

@media (max-width: ${breakpointMedium}) {
  display: none;
}
`;

const RightSideSection = styled.section`
display: flex;
flex-direction: column;
align-items: flex-end;
padding-top: 80px;
padding-right: 40px;
grid-column: 2 / 3;

@media (max-width: ${breakpointMedium}) {
  align-items: center;
  padding: 40px;
  grid-column: 1;
}

@media (max-width: ${breakpointSmall}) {
  padding: 20px 0;
}
`;

const SignBoxDiv = styled.div`
display: inline-block;
width: 468px;
background-color: ${colors.black10};
border-radius: 15px;

@media (max-width: ${breakpointMedium}) {
  width: 100%;
}
`;

const InfoLinksDiv = styled.div`
display: flex;
width: 468px;
justify-content: space-evenly;
margin-top: 30px;

@media (max-width: ${breakpointMedium}) {
  width: 70%;
}

@media (max-width: ${breakpointSmall}) {
  width: 100%;
}
`;

const LinkSpan = styled.span`
color: ${colors.white};
cursor: pointer;
font-size: 15px;
font-weight: 500;
line-height: 12px;
`;

export const tempFindUserAtom = atom<{ email?: string }>({});

const Sign: NextPageWithAppProps<SignProps> = ({ alreadySignedIn }: SignProps) => {
  const router = useRouter();

  const { pageCodePage, page, push } = useSignPageControl();

  const setTempFindUser = useSetAtom(tempFindUserAtom);

  useEffect(() => {
    if (alreadySignedIn && page !== 'resetpassword' && page !== 'resetpasswordconfirm') {
      showToast({ text: '이미 로그인되어 있습니다.' });
      router.replace('/');
    }
  }, [alreadySignedIn, page, router]);

  useEffect(() => () => {
    setTempFindUser({});
  }, [setTempFindUser]);

  useEffect(() => {
    const { alert, ...query } = router.query;
    if (alert === 'need_authentication') {
      router.replace({ query });
      showToast({ text: '로그인이 필요합니다' });
    }
  }, [router]);

  return (
    <SignBaseDiv>
      <Page>
        <SignLayoutDiv>
          <TopDiv>
            <Link href="/">
              <ImageButton src={icGnbWhiteLogo} alt="gnb white logo" />
            </Link>
            {!alreadySignedIn && (
              <GroupSignUpButton onClick={() => push(page === 'organization' ? 'up' : 'organization')}>
                {page === 'organization' ? '일반 회원가입' : '단체 회원가입'}
              </GroupSignUpButton>
            )}
          </TopDiv>
          <LeftSideSection>
            <TitleSpan>통합 회원 계정으로<br />편리하게 이용하세요!</TitleSpan>
            <SubTitleSpan>프로그래머스 인증시험과 프로그래머스는<br />하나의 계정으로 서비스를 모두 이용할 수 있습니다.</SubTitleSpan>
            <ImgContainer><Image src={imgLoginIllust} alt="login illust" /></ImgContainer>
          </LeftSideSection>
          <RightSideSection>
            <SignBoxDiv>
              {pageCodePage ? <SignPage /> : <SignTab />}
            </SignBoxDiv>
            <InfoLinksDiv>
              <a
                href="https://programmers.co.kr/tos"
                target="_blank"
                rel="noreferrer"
              >
                <LinkSpan>이용약관</LinkSpan>
              </a>
              <Divider
                vertical
                color={colors.white}
                opacity={0.4}
              />
              <a
                href="https://programmers.co.kr/privacy"
                target="_blank"
                rel="noreferrer"
              >
                <LinkSpan>개인정보 처리방침</LinkSpan>
              </a>
              <Divider
                vertical
                color={colors.white}
                opacity={0.4}
              />
              <a
                href="https://certi-guide.grepp.co/"
                target="_blank"
                rel="noreferrer"
              >
                <LinkSpan>FAQ/문의</LinkSpan>
              </a>
            </InfoLinksDiv>
          </RightSideSection>
        </SignLayoutDiv>
      </Page>
    </SignBaseDiv>
  );
};

Sign.getLayoutProps = () => ({
  onlyMain: true,
  themeColor: colors.darkBlue2,
});

export default Sign;

export const getServerSideProps = createSigendInCheckGetServerSideProps({
  callback: (isSignedIn, _, { query }) => {
    const { page } = query;

    const pageTitle = (
      page === 'find' && '통합회원 여부 확인' ||
      page === 'in' && '로그인' ||
      page === 'up' && '회원가입' ||
      page?.includes('resetpassword') && '비밀번호 재설정' ||
      '계정 관리'
    );

    return Promise.resolve({
      props: {
        pageTitle,
        alreadySignedIn: isSignedIn,
      },
    });
  },
});
